<template>
  <div class="brands-marketplace-display-wrapper">
    <div v-if="marketplaceDisplay" class="desktop-display-wrapper">
      <div class="info-wrapper">

        <p class="display-title">{{displayName}}</p>

        <div class="buttons-wrapper">
          <div @click="showRequestModalAndGetData"
              class="button button-wrapper"
              :class="{disabled: sponsorUpdated}"
            >
            <fai :icon="['fas', 'comment-dots']" class="mr-1 text-white text-5xl" />
            <span>{{ sponsorUpdated ? 'sponsorship requested' : 'sponsor my campaign' }}</span>
          </div>

          <div @click="clickedInventoryRequests(brandDisplayId)"
            class="button button-wrapper mt-3.5"
            :class="{disabled: supplyUpdated }">
              <fai :icon="['fal', 'box-open']" class="mr-1 text-white text-5xl" />
              <span class="ml-2">{{ supplyUpdated ? 'supply requested' : 'low supply? notify the brand' }}</span>
          </div>
          <div v-if="errors.length">
            <p v-for="(error, index) in errors" :key="index" class="error mb-0">{{ error }}</p>
          </div>
          <simple-modal
            v-if="showRequestModal"
            @rejected="closeModalandResetFields()"
            class="sponsorship-request-modal flex"
            title="sponsor request form" >
              <label>request type:<sup>*</sup></label>
              <div class="checkboxes-wrapper flex justify-between">
                <div class="sponsorship-type-checkbox" v-for="type in sponsorshipTypes" :key="type.id">
                  <checkbox
                    class="sponsorship-checkbox"
                    :checked.sync="type.selected"
                    :class="{ 'sponsor-request-error' : !hasRequestType }"
                  />
                  <label>{{type.name}} </label>
                </div>
              </div>
              <div class="sponsorship-date-range flex flex-row items-center my-2.5">
                <label>date range:</label>
                  <date-range
                    class="sponsorship-date-range-picker px-2"
                    placeholder="choose a date"
                    :class="{'sponsor-request-date-range-border': !hasDateRange}"
                    :start.sync="selectedStartDateRange"
                    :end.sync="selectedEndDateRange"
                    format="M-d-yy"
                    :min="minDateRange"
                  />
              </div>
              <div class="sponsorship-recipient-count grid whitespace-nowrap my-5">
                <label for="suggested_recipient_count">suggested recipient count:</label>
                <input
                  v-model="suggestedRecipientCount"
                  type="number"
                  name="suggested_recipient_count"
                  id="suggested_recipient_count"
                  min="0"
                  :max="textableCount"
                  step="1"
                  :class="{'sponsor-request-error' : !isTextCountValid}"
                  @keypress="sanitizeSuggestedRecipientCount"
                />
                <div class="sub-text text-center text-xs">( current textable members: {{ textableCount }} )</div>
              </div>
              <div class="sponsorship-notes">
                <label> general notes: </label>
                <textarea
                  rows="5"
                  class="sb-input resize-none"
                  type="text"
                  v-model="generalNotes"
                />
              </div>
              <button slot="buttons"
                class="sb-button text-base sm:text-xl py-2 px-8 sm:py-4 sm:px-12"
                type="submit"
                @click="submitRequestModal()">
                submit
              </button>
            </simple-modal>
        </div>
      </div>

      <div class="images-wrapper">
        <div class="secondary-images-wrapper">
          <custom-image
            v-for="image in secondaryImages"
            class="secondary-image"
            :class="[ image.id === selectedImageId ? 'selected' : 'non-selected' ]"
            :image="image"
            :key="image.id"
            @imageClicked="imageSelected"/>
        </div>
        <custom-image class="primary-image"
          :class="{ rounded: !hasSecondaryImages }"
          :image="dynamicMainBrandImg"
        />
      </div>
    </div>

    <simple-modal v-if="marketplaceDisplay && hasMobileScreenSize" class="mobile-display-wrapper">
      <div class="mobile-display-body">
        <button class="button back-button-wrapper" @click="removeMarketplaceDisplay">
          <fai :icon="['fal', 'chevron-left']" class="back-icon"/>
          back
        </button>
        <h2 class="display-title">{{ displayName }}</h2>
        <carousel :slides="mobileSlides.slides"
                  :swiperOptions="swiperOptions"
                  class="mb-10 mobile-carousel">
        >
          <template #swiper-slide-content="{ slide }">
            <img v-if="slide.imageUrl"
                class="slide-image"
                :src="slide.imageUrl"
                :alt="slide.imageUrl">
          </template>
        </carousel>
      </div>
      <div slot="buttons" class="buttons-wrapper">
        <p v-if="supplyUpdated" class="submitted">inventory request submitted</p>
        <button v-else @click="clickedInventoryRequests(brandDisplayId)" class="button inventory">low on supply? click here to notify</button>
        <p v-if="sponsorUpdated" class="submitted">sponsored request submitted</p>
        <button v-else @click="showRequestModalAndGetData" class="button sponsor">sponsor my campaign</button>

        <div v-if="errors.length" class="mobile-errors flex flex-col items-center text-center">
          <p v-for="(error, index) in errors" :key="index" class="error mb-0">{{ error }}</p>
        </div>

        <a target="_blank" href="http://calendly.com/mkilgo" class="button book-demo">need more info?</a>
      </div>
    </simple-modal>
  </div>
</template>

<script>
 import { brandsMarketplaceAPI } from "../../services/api/brandsMarketplaceAPI"
 import { Carousel, CustomImage, SimpleModal, DateRange, Checkbox, UserError, UnexpectedError } from '@springbig/sb-components'
 import Modal from '../shared/Modal'
 import { startOfToday, format } from 'date-fns'



  export default {
    props: {
      marketplaceDisplay: Object
    },

    components: {
      Modal,
      Carousel,
      CustomImage,
      SimpleModal,
      DateRange,
      Checkbox
    },

    name: 'BrandsMarketplaceDisplay',

    data() {
      return {
        dynamicMainBrandImg: null,
        dynamicMainBrandImgId: null,
        displayName: null,
        secondaryImages: null,
        supplyUpdated: false,
        sponsorUpdated: false,
        brandsLoading: false,
        brandDisplayId: null,
        errors: [],
        mobileSlides: {},
        showRequestModal: false,
        swiperOptions: {
          navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev'
          },
          grabCursor: true
        },
        selectedImageId: null,
        hasMobileScreenSize: false,
        selectedStartDateRange: null,
        selectedEndDateRange: null,
        sponsorshipTypes: [],
        textableCount: null,
        suggestedRecipientCount: null,
        generalNotes: null,
        hasRequestType: true,
        hasDateRange: true,
        isTextCountValid: true,
        sponsorRequestIds: [],
      }
    },

    computed: {
      sponsorStartDate(){
       return this.selectedStartDateRange && format(this.selectedStartDateRange, 'yyyy-MM-dd')
      },
      sponsorEndDate(){
       return this.selectedEndDateRange && format(this.selectedEndDateRange, 'yyyy-MM-dd')
      },
      hasSecondaryImages() {
        return !!this.marketplaceDisplay?.secondary_images?.length
      },
      minDateRange() {
        return  startOfToday()
      },
    },

    mounted() {
      this.onBrowserResize()
      window.addEventListener('resize', this.onBrowserResize, { passive: true })
    },

    beforeDestroy() {
      window.removeEventListener('resize', this.onBrowserResize, { passive: true })
    },

    methods: {
      onBrowserResize() {
        this.hasMobileScreenSize = window?.innerWidth < 1024
      },

      imageSelected(selectedImage) {
        this.selectedImageId = selectedImage?.id
        this.dynamicMainBrandImg = selectedImage?.url
      },

      configureDisplay(display) {
        this.displayName = display.name
        this.dynamicMainBrandImg = display.primary_image.url
        this.secondaryImages = this.hasSecondaryImages ? [display.primary_image, ...display.secondary_images] : []
        if (this.hasSecondaryImages) this.selectedImageId = display.primary_image.id
        this.dynamicMainBrandImgId = display.primary_image.id
        this.brandDisplayId = display.id
        this.sponsorUpdated = display.disable_sponsor
        this.supplyUpdated = display.disable_inventory

        this.createSlidesArray('mobileSlides', display.secondary_images)
      },

      createSlidesArray(dataProp, arr) {
        let carousel = {
          slides: []
        }

        arr.forEach((element) => {
          carousel.slides.push({
            label: null,
            imageUrl: element.url,
            id: element.id,
            createdAt: null
          })
        })

        carousel.slides.unshift({
          label: null,
          imageUrl: this.dynamicMainBrandImg,
          id: this.dynamicMainBrandImgId,
          createdAt: null
        })

        this[dataProp] = carousel
      },

      removeMarketplaceDisplay() {
        this.$emit('removeMarketplaceDisplay')
      },

      clickedSideImg(url) {
        this.dynamicMainBrandImg = url
      },

      async showRequestModalAndGetData() {
        this.brandsLoading = true
        try {
          const sponsorTypes = await brandsMarketplaceAPI.getSponsorTypes()
          this.sponsorshipTypes = sponsorTypes.sponsor_request_types.map(value => ({...value, selected: false}))
          const params = {
            allowed_sms_count: Number
          };
          const data = await brandsMarketplaceAPI.getSmsCount(params)
          this.textableCount = data.count
        } catch (error) {
          this.errors = error?.response?.data?.errors?.split(', ') || [error]
        }
        this.brandsLoading = false
        this.showRequestModal = true
      },

      async clickedInventoryRequests(id) {
        this.brandsLoading = true
        try {
          // This is needed to prevent displaying error with ErrorBanner when code 400 hits
          // Code 400 is used so BE can return specific errors
          const res = await brandsMarketplaceAPI.postMarketplaceInventory({brand_display_id: id})
          if (res?.errors) this.errors = res?.errors?.split(', ')
          else this.supplyUpdated = true
        } catch (error) {
          this.errors = error?.response?.data?.errors?.split(', ') || [error]
        }
        this.brandsLoading = false
      },

      closeModalandResetFields() {
        this.showRequestModal = false
        this.hasRequestType = true
        this.isTextCountValid = true
        this.hasDateRange = true
        this.resetFields()
        this.$store.dispatch('DISMISS_ERRORS', {type: UserError})
      },

      resetFields() {
        this.selectedStartDateRange= null
        this.selectedEndDateRange= null
        this.textableCount= null
        this.suggestedRecipientCount= null
        this.generalNotes= null
        this.sponsorshipTypes= []
        this.sponsorRequestIds= []
      },

      sanitizeSuggestedRecipientCount(event) {
        if(event.which < 48 || event.which > 57){event.preventDefault()}
      },

      validateModalFields(){
        this.$store.dispatch('DISMISS_ERRORS', {type: UserError})
        if( this.sponsorRequestIds.length === 0 ) {
          this.$store.dispatch('ADD_ERRORS', [new UserError(new Error('401'), 'sponsor type required')])
          this.hasRequestType = false
        } else {
          this.hasRequestType = true
        }

        if(this.suggestedRecipientCount > this.textableCount){
          this.$store.dispatch('ADD_ERRORS', [new UserError(new Error('401'), 'suggested recipient count can not exceed current textable members')])
          this.isTextCountValid = false
        } else {
          this.isTextCountValid = true
        }

        if(this.selectedStartDateRange && !this.selectedEndDateRange) {
          this.$store.dispatch('ADD_ERRORS', [new UserError(new Error('401'), 'start date and end date required')])
          this.hasDateRange = false
        } else {
          this.hasDateRange = true
        }
      },

      async submitRequestModal(){
        this.sponsorRequestIds = this.sponsorshipTypes.reduce((a,x) => {
          if(x.selected === true) {
            a.push(x.id)
          }
          return a
        }, [])
        this.validateModalFields()
        const areModalFieldsValid = this.hasRequestType && this.isTextCountValid && this.hasDateRange
        if(!areModalFieldsValid) return
        const params = {
          brand_display_id: this.brandDisplayId,
          sponsor_request_log_attributes: {
            start_date: this.sponsorStartDate,
            end_date: this.sponsorEndDate,
            suggested_recipient_count: this.suggestedRecipientCount,
            general_notes: this.generalNotes,
            sponsor_request_type_ids: this.sponsorRequestIds
          }
        }
        try {
          this.$store.dispatch('DISMISS_ERRORS', { type: UnexpectedError })
          const response =  await brandsMarketplaceAPI.postMarketplaceSponsor(params)
          if(response?.errors){
            const unexpectedErrors = response.errors.sponsor_requests.map(error => new UnexpectedError(new Error('422') , error))
            this.$store.dispatch('ADD_ERRORS', unexpectedErrors)
          }
          else {
            this.closeModalandResetFields()
            this.sponsorUpdated = true
          }
        } catch (error) {
          this.$store.dispatch('ADD_ERRORS', [new UnexpectedError(error[0])])
        }
      }
    },

    created() {
      if (!!this.marketplaceDisplay) this.configureDisplay(this.marketplaceDisplay)
    },

    watch: {
      marketplaceDisplay() {
        if (!!this.marketplaceDisplay) this.configureDisplay(this.marketplaceDisplay)
      }
    }
  }
</script>

<style scoped>
/*------------------------------ STYLES FOR DESKTOP LAYOUT ------------------------------*/
.desktop-display-wrapper {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.desktop-display-wrapper .info-wrapper {
  margin-right: 20px;
}

.desktop-display-wrapper .display-title {
  font-family: 'AileronLight', sans-serif;
  font-size: 1.5em;
  word-break: break-all;
}

.desktop-display-wrapper .button-wrapper {
  border-radius: 10px;
  display: flex;
  font-size: 1em;
  font-weight: bold;
  line-height: 1.5em;
  padding: 10px;
  text-align: center;
  width: 200px;
  background: #128ff9;
}

.desktop-display-wrapper .button-wrapper:not(:first-child) {
  margin-top: 10px;
}

.desktop-display-wrapper .button-wrapper.disabled {
  opacity: 0.50;
  cursor: revert;
  pointer-events: none;
}

.images-wrapper {
  display: flex;
}

.secondary-images-wrapper {
  display: grid;
  grid-template-rows: repeat(6, 1fr);
  height: 280px;
  width: 55px;
}

.secondary-image {
  border-bottom-left-radius: 15px;
  border-top-left-radius: 15px;
  border: 2px solid #128ff9;
  cursor: pointer;
  overflow: hidden;
}

.secondary-image.selected {
  border-bottom-width: 3px;
  border-left-width: 3px;
  border-top-width: 3px;
}

.secondary-image.selected:not(:first-child) {
  margin-top: -2px;
}

.secondary-image.non-selected:not(:first-child) {
  margin-top: -2px;
}

.secondary-image::v-deep img {
  width: 100%;
  height: 100%;
  object-fit: fill;
}

.primary-image {
  border: 2px solid #128ff9;
  border-top-right-radius: 20px;
  border-bottom-right-radius: 20px;
  overflow: hidden;
  width: 280px;
  height: 280px;
  border-left-width: 1px;
}

/* Have to use width & height here to use object-fit */
.primary-image::v-deep img {
  width: 100%;
  height: 100%;
  object-fit: fill;
}

.primary-image.rounded {
  border-radius: 20px;
  border-left-width: 2px;
}

/*------------------------------ STYLES FOR MOBILE LAYOUT ------------------------------*/
.mobile-display-wrapper.simple-modal {
  background: white;
  padding: unset;
  display: none;
  overflow: hidden;
}

.mobile-display-wrapper::v-deep header {
  display: none;
  overflow: hidden;
}

.mobile-display-wrapper::v-deep .rounded-3xl.bg-white.p-4.relative.border.border-solid.max-h-full.overflow-y-auto {
  height: 100%;
  width: 100%;
  border: none;
}

.mobile-display-wrapper::v-deep .swiper-button-disabled {
  opacity: 0;
}

.mobile-display-wrapper::v-deep .display-title {
  font-size: 2em;
  margin: 50px auto;
  overflow: hidden;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 80%;
  font-family: 'AileronLight', sans-serif;
  font-weight: bold;
}

.mobile-display-wrapper::v-deep img {
  width: 200px;
  height: 200px;
  border: 2px solid #128ff9;
  border-radius: 20px;
}

.mobile-display-wrapper::v-deep .back-button-wrapper {
  background: white;
  border: 1px solid #128ff9;
  color: #128ff9;
  border-radius: 100px;
  cursor: pointer;
}

.mobile-display-wrapper::v-deep .back-button-wrapper:hover {
  border: 1px solid #14679e;
  color: #14679e;
}

.mobile-display-wrapper::v-deep .buttons-wrapper {
  display: flex;
  flex-direction: column;
  width: 255px;
}

.mobile-display-wrapper::v-deep .button:not(:first-child) {
  margin-top: 15px;
}

.mobile-display-wrapper::v-deep .submitted {
  color: #32CD32;
  text-align: center;
}

/*------------------------------ MEDIA QUERIES ------------------------------*/
@media only screen and (max-width: 1023px) {
  .desktop-display-wrapper {
    display: contents;
    width: fit-content;
  }
  .mobile-display-wrapper.simple-modal {
    display: flex;
    overflow: auto;
    z-index: 2;
  }
  .buttons-wrapper .button,
  .mobile-errors {
    font-size: 1rem;
  }

  .sponsorship-request-modal .sponsorship-type-checkbox {
    display: flex;
    flex-flow: row wrap;
    justify-content: flex-start;
  }

  .sponsorship-date-range-picker::v-deep .text-subtitle {
    white-space: nowrap;
  }

}

@media only screen and (max-width: 568px) {
  .mobile-display-wrapper::v-deep .buttons-wrapper {
    width: 90%;
  }
  .mobile-display-wrapper::v-deep .button,
  .mobile-display-wrapper::v-deep .submitted {
    font-size: 20px;
    font-weight: 600;
  }
  .mobile-errors {
    font-size: 20px;
  }

  .checkboxes-wrapper {
    display:grid;
    grid-template-columns: 1.25fr 1fr 1.25fr;
  }
}

@media only screen and(max-width: 410px) {
  .checkboxes-wrapper {
    grid-template-columns: repeat(2, 1fr);
  }
}
/*------------------------------ SPONSORSHIP MODAL ------------------------------*/
.sponsorship-type-checkbox{
  display: grid;
  grid-auto-flow: column;
  align-items: center;
}

.sponsorship-checkbox{
  margin: 5px;
}

.sponsorship-recipient-count{
  grid-template-columns: 185px auto;
  grid-template-rows: 25px;
}

.sponsorship-recipient-count::v-deep input{
  width: 100px;
  height: 30px;
  margin-left: 5px;
}

.sponsorship-date-range-picker::v-deep .text-subtitle{
  opacity: 1;
  font-weight: bold;
}

.sponsorship-date-range-picker::v-deep .sb-input {
  padding-left: 20px;
  padding-right: 20px;
  height: 30px;
  align-items: center;
  display: flex;
  color:#128ff9;
}

.sponsorship-notes::v-deep label{
  margin-bottom: 4px;
}

.sponsorship-date-range-picker::v-deep .start-str{
 margin-right: 5px;
}

.sponsorship-date-range-picker::v-deep .end-str{
  margin-left: 5px;
}

.sponsorship-request-modal::v-deep .sponsor-request-error {
  border-color: red;
}

.sponsorship-date-range .sponsor-request-date-range-border::v-deep .flatpickr-input{
  border-color: red;
  color: red;
}
</style>
